import type { Caller } from "./caller";

/**
 * Which surface a role lives on (pure, no I/O), shared by every client.
 *
 * These two functions were `src/app/_components/roleHome.ts` and
 * `consumerSurface.ts` until R22 T10. They moved because the static web app
 * needs the **same** answers the server gives: a route guard that decides
 * "members do not browse the consumer side" in the client, from its own copy of
 * the rule, is a second implementation of H28 waiting to disagree with the
 * first. One function, two callers, no drift.
 *
 * **The paths are presentation and that is fine.** `/business` and `/admin` are
 * the same URLs on both frontends by construction (the SPA reproduces today's
 * routes), so putting them here costs nothing and saves a mapping table. What
 * this file must never gain is a decision the server does not re-take: a client
 * that redirects is a courtesy, and every guarded read still re-asks §5.
 */

/** The four roles a resolved caller can have. A guest is the absence of one. */
export type SurfaceRole = Caller["kind"];

/** Where each role "lives" (docs/ux/screens/LogInScreen.md, happy path). */
export function roleHome(kind: SurfaceRole): string {
  switch (kind) {
    case "admin":
      return "/admin";
    case "member":
    case "unaffiliated":
      return "/business";
    case "consumer":
      return "/";
  }
}

/**
 * H28, consumer-surface access policy.
 *
 * The landing/home, discovery (search), and the booking flow are for Consumers
 * and logged-out visitors only. A logged-in non-consumer, a business/practitioner
 * member (either tier), an unaffiliated member, or an admin, is redirected to
 * their own home (roleHome): business and practitioner users neither browse nor
 * book on the consumer side, and this includes their own business's public
 * profile (no member-facing public-profile preview, the "View public profile"
 * affordance was removed). The role is exclusive: an account is a member OR a
 * Consumer, never both, and the request command is consumer-only anyway.
 *
 * Takes only the role, not the whole Caller: the client side of this knows a
 * `WireMe`, not a `Caller`, and the rule never needed anything else.
 *
 * @returns the path to redirect a blocked caller to, or null when the caller
 *   may stay (a Consumer or a logged-out guest).
 */
export function consumerSurfaceRedirect(
  caller: { kind: SurfaceRole } | null,
): string | null {
  // Guests and Consumers are the intended audience, always allowed.
  if (!caller || caller.kind === "consumer") return null;
  // Every other role (member, unaffiliated, admin) goes to its own home.
  return roleHome(caller.kind);
}
